import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import BrandMark from '../components/BrandMark';
import brandStyles from '../styles/BrandMark.module.css';
import useAuthPageBackground from '../hooks/useAuthPageBackground';
import styles from '../styles/LoginPage.module.css';
import { TIMEZONES } from '../utils/constants';
import { validateEmail, validatePhone } from '../utils/validation';
import api from '../api';
import { completeOnboarding } from '../redux/authSlice';

function OnboardPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  useAuthPageBackground();

  const [checking, setChecking] = useState(true);
  const [inviteError, setInviteError] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    phone: '',
    discord_username: '',
    timezone: 'America/New_York'
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.getOnboardingInvite(token);
        if (cancelled) return;
        setForm((prev) => ({
          ...prev,
          phone: data.prefill?.phone || '',
          discord_username: data.prefill?.discord_username || ''
        }));
        setInviteError('');
      } catch (err) {
        if (!cancelled) {
          setInviteError(err.response?.data?.error || 'This invite link is invalid or has expired.');
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');

    if (form.password !== form.confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }
    if (form.email) {
      const emailError = validateEmail(form.email);
      if (emailError) {
        setFormError(emailError);
        return;
      }
    }
    if (form.phone) {
      const phoneError = validatePhone(form.phone);
      if (phoneError) {
        setFormError(phoneError);
        return;
      }
    }

    setSubmitting(true);
    try {
      await dispatch(completeOnboarding({
        token,
        username: form.username,
        password: form.password,
        email: form.email || undefined,
        phone: form.phone || undefined,
        discord_username: form.discord_username || undefined,
        timezone: form.timezone
      })).unwrap();
      navigate('/');
    } catch (err) {
      setFormError(typeof err === 'string' ? err : (err?.message || 'Could not create your account.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className={styles.container}>
        <div className={styles.formWrapper}>
          <p className={styles.subtitle}>Checking your invite…</p>
        </div>
      </div>
    );
  }

  if (inviteError) {
    return (
      <div className={styles.container}>
        <div className={styles.formWrapper}>
          <BrandMark size={56} showText className={`${brandStyles.centered} ${brandStyles.authMark}`} />
          <h1 className={styles.title}>Invite expired</h1>
          <p className={styles.subtitle}>{inviteError}</p>
          <p className={styles.formFooter}>
            Ask a commissioner for a new link, then <Link to="/login" className={styles.link}>sign in</Link> if you already have an account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.formWrapper}>
        <BrandMark size={56} showText className={`${brandStyles.centered} ${brandStyles.authMark}`} />
        <h1 className={styles.title}>Create your account</h1>
        <p className={styles.subtitle}>Username and password are required. Everything else is optional.</p>

        {formError && <div className={styles.errorMessage}>{formError}</div>}

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.inputGroup}>
            <label htmlFor="username">Username</label>
            <input id="username" name="username" value={form.username} onChange={handleChange} required className={styles.input} autoComplete="username" />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="password">Password</label>
            <input id="password" name="password" type="password" value={form.password} onChange={handleChange} required minLength={8} className={styles.input} autoComplete="new-password" />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="confirmPassword">Confirm password</label>
            <input id="confirmPassword" name="confirmPassword" type="password" value={form.confirmPassword} onChange={handleChange} required className={styles.input} autoComplete="new-password" />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="email">Email (optional)</label>
            <input id="email" name="email" type="email" value={form.email} onChange={handleChange} className={styles.input} autoComplete="email" />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="phone">Phone (optional)</label>
            <input id="phone" name="phone" value={form.phone} onChange={handleChange} className={styles.input} autoComplete="tel" />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="discord_username">Discord username (optional)</label>
            <input id="discord_username" name="discord_username" value={form.discord_username} onChange={handleChange} className={styles.input} />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="timezone">Timezone</label>
            <select id="timezone" name="timezone" value={form.timezone} onChange={handleChange} className={styles.input}>
              {TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>
          <button type="submit" className={styles.primaryButton} disabled={submitting}>
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <div className={styles.formFooter}>
          <p>Already have an account? <Link to="/login" className={styles.link}>Sign In</Link></p>
        </div>
      </div>
    </div>
  );
}

export default OnboardPage;
